import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { Btn } from '../../components/Btn';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { ReceiptPreview } from '../../components/ReceiptPreview';
import { getSettings } from '../../data/fetchers';
import { saveSettings } from '../../data/mutators';
import { canEditSystemConfig } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { SystemSettings } from '../../types/domain';

export function SystemSettingsView() {
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditSystemConfig(user) : false;
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  });

  if (!canEdit) {
    return (
      <div className="bo-fade">
        <PageHeader title="ตั้งค่าระบบ" />
        <div className="bo-card">
          <EmptyState
            title="เฉพาะเจ้าของ"
            sub="การตั้งค่าระบบทำได้โดยผู้มีสิทธิ์ระบบเท่านั้น"
          />
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="bo-fade">
        <PageHeader title="ตั้งค่าระบบ" subtitle="กำลังโหลด..." />
        <div className="bo-card h-40 animate-pulse" />
      </div>
    );
  }

  return <SystemSettingsForm initialSettings={data} />;
}

function SystemSettingsForm({
  initialSettings,
}: {
  initialSettings: SystemSettings;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<SystemSettings>(initialSettings);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: saveSettings,
    onSuccess: (savedSettings) => {
      // Write the saved value straight into the cache: instant propagation to this view
      // + the VAT report (same ['settings'] key) with NO background refetch (avoids the
      // out-of-act re-render that invalidateQueries caused). Cross-tab still re-syncs via
      // the storage listener invalidating ['settings'] in the other tab.
      qc.setQueryData(['settings'], savedSettings);
      setSaved(true);
    },
  });

  const set = <K extends keyof SystemSettings>(k: K, v: SystemSettings[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  return (
    <div className="bo-fade">
      <PageHeader title="ตั้งค่าระบบ" subtitle="ข้อมูลร้าน · ภาษี · ใบเสร็จ" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={onSubmit} className="bo-card p-6 flex flex-col gap-6">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm-label font-semibold text-ink-2">
              ชื่อร้าน
            </span>
            <input
              aria-label="ชื่อร้าน"
              value={form.shopName}
              onChange={(e) => set('shopName', e.target.value)}
              className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm-label font-semibold text-ink-2">
              เลขผู้เสียภาษี
            </span>
            <input
              aria-label="เลขผู้เสียภาษี"
              value={form.taxId}
              onChange={(e) => set('taxId', e.target.value)}
              className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm-label font-semibold text-ink-2">
              อัตรา VAT (%)
            </span>
            <input
              aria-label="อัตรา VAT (%)"
              type="number"
              value={Math.round(form.vatRate * 100)}
              onChange={(e) => set('vatRate', Number(e.target.value) / 100)}
              className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
            />
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              aria-label="ราคารวม VAT แล้ว"
              type="checkbox"
              checked={form.vatInclusive}
              onChange={(e) => set('vatInclusive', e.target.checked)}
              className="w-4.5 h-4.5 rounded border-line text-primary focus:ring-primary/30 accent-primary cursor-pointer"
            />
            <span className="text-sm-label font-semibold text-ink-2">
              ราคารวม VAT แล้ว
            </span>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm-label font-semibold text-ink-2">
              หัวใบเสร็จ
            </span>
            <input
              aria-label="หัวใบเสร็จ"
              value={form.receiptHeader}
              onChange={(e) => set('receiptHeader', e.target.value)}
              className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm-label font-semibold text-ink-2">
              ท้ายใบเสร็จ
            </span>
            <input
              aria-label="ท้ายใบเสร็จ"
              value={form.receiptFooter}
              onChange={(e) => set('receiptFooter', e.target.value)}
              className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
            />
          </label>
          <div className="flex items-center gap-3 justify-end pt-2">
            {saved && (
              <span className="text-sm-label text-ok font-semibold">
                บันทึกแล้ว
              </span>
            )}
            <Btn kind="primary" type="submit">
              บันทึก
            </Btn>
          </div>
        </form>
        <div className="flex justify-center lg:justify-start">
          <ReceiptPreview s={form} />
        </div>
      </div>
    </div>
  );
}
